// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatStep, MatStepper } from '@angular/material/stepper';
import { ActivatedRoute, Router } from '@angular/router';
import { SystemDocument } from '@interfaces/document.interfaces';
import { User, UserLogonState, UserNotificationType, UserSignatureCall, UserTypes } from '@interfaces/user.interfaces';
import { UserService } from '@services/user.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable, forkJoin, from, of, switchMap, takeWhile } from 'rxjs';
import { LoginService } from 'src/app/login/services/login.service';
import { formatSignatureRequest } from 'src/app/utils/format-util';
import { getDealerHomePage, scrollIntoView } from 'src/app/utils/utils';

@Component({
    selector: 'set-up',
    templateUrl: './set-up.component.html',
    styleUrls: ['./set-up.component.scss'],
})
export class SetUpComponent implements OnInit, OnDestroy {
    @ViewChild('stepper', { static: true }) private stepper: MatStepper;
    private agreements: SystemDocument[] = [];
    public step1: MatStep;
    public step2: MatStep;
    public step3: MatStep;
    public step1Label: string = null;
    public step2Label: string = null;
    public step3Label: string = null;
    public user: User = null;
    private isComponentAlive = true;
    public isLoading = false;
    private notificationType: UserNotificationType = UserNotificationType.Both;
    public requiredDocs: SystemDocument[] = [];
    public showSign = false;
    public canEditSignatures = false;
    private updatedSignature: any;
    public formDataInitials: FormData = new FormData();
    public formDataSignature: FormData = new FormData();
    public versionNumber: string = null;
    public next: string = null;

    get needSignatures(): boolean {
        return this.user?.needProfileUpdate || false;
    }

    constructor(
        private readonly route: ActivatedRoute,
        private readonly deviceService: DeviceDetectorService,
        private readonly router: Router,
        private readonly loginService: LoginService,
        private readonly userService: UserService
    ) {
        route.params.pipe(takeWhile(() => this.isComponentAlive)).subscribe((activatedRouteParams) => {
            this.next = decodeURIComponent(activatedRouteParams?.next ?? '');
        });
    }

    ngOnInit() {
        this.setLabels();
        this.user = this.route.snapshot.data.user;
        this.requiredDocs = this.route.snapshot.data.requiredDocs;

        // Precaution in case something happened retrieving user from resolver
        if (!this.user) {
            this.getUserDetails();
        }
    }

    ngOnDestroy(): void {
        this.isComponentAlive = false;
    }

    public eventHandler(event: any): void {
        switch (event.context) {
            case 'changed-values':
                this.isLoading = true;
                this.updatedSignature = event.data;
                this.saveSignatures();
                break;
            case 'loading-start':
                this.isLoading = true;
                break;
            case 'loading-done':
                setTimeout(() => {
                    this.isLoading = false;
                }, 500);
                break;
            case 'notification':
                this.notificationType = event.data;
                break;
            case 'step-1-complete':
                this.stepper.selected.completed = true;
                this.stepper.next();
                break;
            case 'step-2-complete':
                this.agreements = event.data;
                this.saveSystemDocs();

                if (this.needSignatures) {
                    this.stepper.selected.completed = true;
                    this.stepper.next();
                    this.showSign = true;
                    scrollIntoView('#step-3-signature');
                    // Give the view a moment to actually render the canvas for the signatures
                    // and allow the capture component to scale properly
                    setTimeout(() => (this.canEditSignatures = true), 100);
                    this.isLoading = false;
                } else {
                    this.saveSignatures();
                }

                break;
            case 'step-2-go-back':
                this.stepper.selected.completed = true;
                this.stepper.previous();
                this.isLoading = false;
                break;
            default:
                break;
        }
    }

    private getUserDetails(): any {
        this.userService
            .getUser$()
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((user: any) => {
                if (user) {
                    this.user = user;
                }
            });
    }

    public saveSystemDocs(): void {
        this.saveAgreements()
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((val) => {
                if (val) {
                    this.loginService
                        .logLoginState(this.user.userId, this.user.logonId, UserLogonState.SystemDocumentsAccepted)
                        .pipe(takeWhile(() => this.isComponentAlive))
                        .subscribe();
                }
            });
    }

    public saveSignatures(): void {
        if (this.needSignatures) {
            this.saveSignaturesUpdateNotifications();
        } else {
            this.navigateIntoApp();
        }
    }

    public saveSignaturesUpdateNotifications() {
        forkJoin([
            from(formatSignatureRequest('signature', this.updatedSignature, this.user.dealerId)).pipe(
                switchMap((request) => this.userService.createSignature$(UserSignatureCall.signature, request))
            ),
            from(formatSignatureRequest('initials', this.updatedSignature, this.user.dealerId)).pipe(
                switchMap((request) => this.userService.createSignature$(UserSignatureCall.initials, request))
            ),
        ])
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((val) => {
                if (val) {
                    this.userService
                        .updateUserNotification$(
                            this.userService.user.email,
                            this.userService.user.firstName,
                            this.userService.user.lastName,
                            this.userService.user.phone,
                            UserNotificationType[this.notificationType],
                            this.userService.user.dealerId
                        )
                        .pipe(
                            switchMap((user) => {
                                // now that signatures/notifications/etc are all complete, track a successful login.
                                // without this step, the user will see the /welcome flow again on their next login
                                return user ? this.loginService.logLoginState(user.userId, user.logonId, UserLogonState.SignaturesUpdated) : of(null);
                            })
                        )
                        .subscribe((res) => {
                            if (res) {
                                this.navigateIntoApp();
                            }
                        });
                }
                this.isLoading = false;
            });
    }

    public navigateIntoApp() {
        this.loginService.logLoginState(this.user.userId, this.user.logonId, UserLogonState.Success).subscribe();
        if (!this.next) {
            this.userService.user.userType === UserTypes.Customer
                ? this.router.navigate(['/main', 'vehicles'])
                : this.router.navigate(
                      getDealerHomePage(this.userService.user.defaultPageId, this.userService.isCustomer, this.userService.user.dealerAppPermissions)
                  );
        } else {
            this.router.navigate(['/' + this.next]);
        }
    }

    private saveAgreements(): Observable<SystemDocument[][]> {
        const agreed = this.agreements.map((agreement) => this.userService.updateRequireSystemDoc(agreement.currentSystemDocumentId));

        // Still return an observable if we have nothing to update
        return agreed.length > 0 ? forkJoin(agreed) : of(null);
    }

    private setLabels(): void {
        if (this.deviceService.isDesktop()) {
            this.step1Label = 'Getting Started';
            this.step2Label = 'Agreements';
            this.step3Label = 'E-Sign Set Up';
        } else {
            this.step1Label = 'Getting Started';
            this.step2Label = 'Agreements';
            this.step3Label = 'E-Sign Set Up';
        }
    }
}
